import { LoggerService } from '../../../shared/services/logger.service';
import {
  DisplayedPromotionDetails,
  PromotionHistory,
} from './models/promotion-history';
import { LocalStorage } from '../../../shared/utils/storage/local-storage';
// import { Game } from '../../models/game.model';
import { singleton } from 'tsyringe';
import { RecommendationConfig } from '../datastore-service/models/recommendation-config.model';
import { Game } from '../../models/game.model';
import { Promotion } from '../../models/promotion.model';
import { SessionStorage } from '../../../shared/utils/storage/session-storage';
import { PromotionType } from '../datastore-service/models/raw-promotion.model';
// import { Application } from '../../models/application.model';

const kNameOfPromotionHistory = 'promotion-history';
const kNameOfPromotionGeo = 'promotion-geo';
const kNameOfPromotionAdUnits = 'promotion-ad-units';
const kNameOfGlobalConfigurations = 'promotion-global-configurations';
const kAdUnitDisplayedPrefix = 'adUnitPromotion_';

export interface PromotionAdUnits {
  adUnits: { [key: string]: PromotionAdUnit };
}
export interface PromotionAdUnit {
  promotionId: string;
  views: number;
  lastViewTimestamp: number;
}

// -----------------------------------------------------------------------------
@singleton()
export class PersistentStorageService {
  private readonly logger = LoggerService.getCategory(
    'PersistentStorageService',
  );

  // ---------------------------------------------------------------------------
  public deleteLocalStorageItems() {
    localStorage.clear();
  }

  // ---------------------------------------------------------------------------
  // Global configurations
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  public getGlobalConfiguration(): RecommendationConfig {
    const config = LocalStorage.get(
      kNameOfGlobalConfigurations,
    ).asObject() as RecommendationConfig;

    if (!config) {
      this.logger.error('Promotion global configurations were not found');
      return null;
    }

    return config;
  }

  // ---------------------------------------------------------------------------
  public setGlobalConfiguration(config: RecommendationConfig) {
    LocalStorage.setObject(kNameOfGlobalConfigurations, config);
  }

  // ---------------------------------------------------------------------------
  // Last displayed promotion timestamp
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  public getLastDisplayedPromotionTimestamp() {
    const PromotionsHistory = this.getPromotionsHistory();
    return PromotionsHistory?.lastDisplayedPromotionTimestamp;
  }

  // ---------------------------------------------------------------------------
  public setLastDisplayedPromotionTimestamp(timestamp = Date.now()) {
    const PromotionsHistory = this.getPromotionsHistory();
    PromotionsHistory.lastDisplayedPromotionTimestamp = timestamp;
    this.setPromotionsHistory(PromotionsHistory);
  }

  // ---------------------------------------------------------------------------
  public resetLastDisplayedPromotionTimestamp() {
    const PromotionsHistory = this.getPromotionsHistory();
    PromotionsHistory.lastDisplayedPromotionTimestamp = 0;
    this.setPromotionsHistory(PromotionsHistory);
  }

  // ---------------------------------------------------------------------------
  // Last displayed offer timestamp
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  public getLastDisplayedOfferTimestamp() {
    const PromotionsHistory = this.getPromotionsHistory();
    return PromotionsHistory?.lastDisplayedOfferTimestamp;
  }

  // ---------------------------------------------------------------------------
  public setLastDisplayedOfferTimestamp(timestamp = Date.now()) {
    const PromotionsHistory = this.getPromotionsHistory();
    PromotionsHistory.lastDisplayedOfferTimestamp = timestamp;
    this.setPromotionsHistory(PromotionsHistory);
  }

  // ---------------------------------------------------------------------------
  public resetLastDisplayedOfferTimestamp() {
    const PromotionsHistory = this.getPromotionsHistory();
    PromotionsHistory.lastDisplayedOfferTimestamp = 0;
    this.setPromotionsHistory(PromotionsHistory);
  }

  // ---------------------------------------------------------------------------
  // Displayed Promotions
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  public getDisplayedPromotionInfo(
    game: Game,
    promotion: Promotion,
  ): DisplayedPromotionDetails {
    const promotionsHistory = this.getPromotionsHistory();

    const displayedApps = promotionsHistory.displayedAppsPerGame;
    if (!displayedApps) {
      return null;
    }

    const foundGame = displayedApps[game.id];

    if (!foundGame) {
      return null;
    }

    const appInfo = foundGame[promotion.id];

    if (!appInfo) {
      return null;
    }

    return appInfo;
  }

  // ---------------------------------------------------------------------------
  public setPromotionDisplayed(
    gameId: number,
    promotionId: string,
    countOverride = 0,
  ) {
    const promotionsHistory = this.getPromotionsHistory();

    let displayAppsPerGame = promotionsHistory.displayedAppsPerGame;
    if (!displayAppsPerGame) {
      promotionsHistory.displayedAppsPerGame = {};
      displayAppsPerGame = promotionsHistory.displayedAppsPerGame;
    }

    let displayedAppsForGame = displayAppsPerGame[gameId];
    if (!displayedAppsForGame) {
      promotionsHistory.displayedAppsPerGame[gameId] = {};
      displayedAppsForGame = promotionsHistory.displayedAppsPerGame[gameId];
    }

    if (!displayedAppsForGame[promotionId]) {
      displayedAppsForGame[promotionId] = {
        lastTimestamp: 0,
        count: 0,
        CTAClicked: 0,
      };
    }

    const displayDetails = displayedAppsForGame[promotionId];
    displayDetails.lastTimestamp = Date.now();
    displayDetails.count =
      countOverride > 0 ? countOverride : displayDetails.count + 1;
    this.setPromotionsHistory(promotionsHistory);
  }

  // ---------------------------------------------------------------------------
  public setPromotionClicked(gameId: number, promotionId: string) {
    const promotionsHistory = this.getPromotionsHistory();

    let displayAppsPerGame = promotionsHistory.displayedAppsPerGame;
    if (!displayAppsPerGame) {
      promotionsHistory.displayedAppsPerGame = {};
      displayAppsPerGame = promotionsHistory.displayedAppsPerGame;
    }

    let displayedAppsForGame = displayAppsPerGame[gameId];
    if (!displayedAppsForGame) {
      promotionsHistory.displayedAppsPerGame[gameId] = {};
      displayedAppsForGame = promotionsHistory.displayedAppsPerGame[gameId];
    }

    if (!displayedAppsForGame[promotionId]) {
      displayedAppsForGame[promotionId] = {
        lastTimestamp: 0,
        count: 0,
        CTAClicked: 0,
      };
    }

    const displayDetails = displayedAppsForGame[promotionId];
    displayDetails.CTAClicked++;
    this.setPromotionsHistory(promotionsHistory);
  }

  // ---------------------------------------------------------------------------
  public resetDisplayedPromotionCount(game: Game, promotion: Promotion) {
    const promotionsHistory = this.getPromotionsHistory();

    const displayedAppsForGame =
      promotionsHistory.displayedAppsPerGame[game.id];
    if (!displayedAppsForGame) {
      return;
    }

    if (displayedAppsForGame[promotion.id]) {
      delete promotionsHistory.displayedAppsPerGame[game.id][promotion.id];
    }

    this.setPromotionsHistory(promotionsHistory);
  }

  // ---------------------------------------------------------------------------
  public resetDisplayedPromotions() {
    const promotionsHistory = this.getPromotionsHistory();

    promotionsHistory.displayedAppsPerGame = {};
    this.setPromotionsHistory(promotionsHistory);
  }

  // ---------------------------------------------------------------------------
  // Displayed Promotions - Offers
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  public getDisplayedPromotionOfferInfo(game: Game): DisplayedPromotionDetails {
    const promotionsHistory = this.getPromotionsHistory();

    const displayedOffers = promotionsHistory.displayedOffersPerGame;
    if (!displayedOffers) {
      return null;
    }

    const foundPromotion = displayedOffers[game.id];
    if (!foundPromotion) {
      return null;
    }

    return foundPromotion;
  }

  // ---------------------------------------------------------------------------
  public setPromotionOfferDisplayed(gameId: number, countOverride = 0) {
    const promotionsHistory = this.getPromotionsHistory();

    let displayOffersPerGame = promotionsHistory.displayedOffersPerGame;
    if (!displayOffersPerGame) {
      promotionsHistory.displayedOffersPerGame = {};
      displayOffersPerGame = promotionsHistory.displayedOffersPerGame;
    }

    let displayedOffer = displayOffersPerGame[gameId];
    if (!displayedOffer) {
      promotionsHistory.displayedOffersPerGame[gameId] = {
        lastTimestamp: 0,
        count: 0,
        CTAClicked: 0,
        dismissedTimestamp: 0,
        approvedTimestamp: 0,
      };
      displayedOffer = promotionsHistory.displayedOffersPerGame[gameId];
    }

    displayedOffer.lastTimestamp = Date.now();
    displayedOffer.count =
      countOverride > 0 ? countOverride : displayedOffer.count + 1;
    this.setPromotionsHistory(promotionsHistory);
  }

  // ---------------------------------------------------------------------------
  public setPromotionDismissed(gameId: number) {
    const promotionsHistory = this.getPromotionsHistory();

    let displayOffersPerGame = promotionsHistory.displayedOffersPerGame;
    if (!displayOffersPerGame) {
      promotionsHistory.displayedOffersPerGame = {};
      displayOffersPerGame = promotionsHistory.displayedOffersPerGame;
    }

    let displayedOffer = displayOffersPerGame[gameId];
    if (!displayedOffer) {
      promotionsHistory.displayedOffersPerGame[gameId] = {
        lastTimestamp: 0,
        count: 0,
        CTAClicked: 0,
        dismissedTimestamp: 0,
        approvedTimestamp: 0,
      };
      displayedOffer = promotionsHistory.displayedOffersPerGame[gameId];
    }

    displayedOffer.dismissedTimestamp = Date.now();
    this.setPromotionsHistory(promotionsHistory);
  }

  // ---------------------------------------------------------------------------
  public setPromotionApproved(gameId: number) {
    const promotionsHistory = this.getPromotionsHistory();

    let displayOffersPerGame = promotionsHistory.displayedOffersPerGame;
    if (!displayOffersPerGame) {
      promotionsHistory.displayedOffersPerGame = {};
      displayOffersPerGame = promotionsHistory.displayedOffersPerGame;
    }

    let displayedOffer = displayOffersPerGame[gameId];
    if (!displayedOffer) {
      promotionsHistory.displayedOffersPerGame[gameId] = {
        lastTimestamp: 0,
        count: 0,
        CTAClicked: 0,
        dismissedTimestamp: 0,
        approvedTimestamp: 0,
      };
      displayedOffer = promotionsHistory.displayedOffersPerGame[gameId];
    }

    displayedOffer.approvedTimestamp = Date.now();
    this.setPromotionsHistory(promotionsHistory);
  }

  // ---------------------------------------------------------------------------
  public setPromotionDisplayedByType(
    gameId: number,
    promotionId: string,
    promotionType: PromotionType,
  ) {
    if (promotionType && promotionType === PromotionType.Offer) {
      this.setPromotionOfferDisplayed(gameId);
    } else {
      this.setPromotionDisplayed(gameId, promotionId);
    }
  }

  // ---------------------------------------------------------------------------
  // PromotionHistory
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  public getPromotionsHistory(): PromotionHistory {
    const promotionsHistory = LocalStorage.get(
      kNameOfPromotionHistory,
    ).asObject() as PromotionHistory;

    if (promotionsHistory) {
      return promotionsHistory;
    }

    return this.generateNewPromotionsHistory();
  }

  // ---------------------------------------------------------------------------
  public createPromotionHistory() {
    const promotionsHistory = this.generateNewPromotionsHistory();
    this.setPromotionsHistory(promotionsHistory);
  }

  // ---------------------------------------------------------------------------
  public saveGeoResult(geoCountryCode: string) {
    SessionStorage.set(kNameOfPromotionGeo, geoCountryCode);
  }

  // ---------------------------------------------------------------------------
  public getGeoResult() {
    return SessionStorage.get(kNameOfPromotionGeo).raw;
  }

  // ---------------------------------------------------------------------------
  private generateNewPromotionsHistory(): PromotionHistory {
    return {
      lastDisplayedPromotionTimestamp: 0,
      lastDisplayedOfferTimestamp: 0,
      displayedAppsPerGame: {},
      displayedOffersPerGame: {},
    };
  }

  // ---------------------------------------------------------------------------
  private setPromotionsHistory(promotionsHistory: PromotionHistory) {
    LocalStorage.setObject(kNameOfPromotionHistory, promotionsHistory);
  }

  // ---------------------------------------------------------------------------
  // PromotionAdUnits
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  public getPromotionAdUnits(): PromotionAdUnits {
    const promotionAdUnits = LocalStorage.get(
      kNameOfPromotionAdUnits,
    ).asObject() as PromotionAdUnits;

    if (!promotionAdUnits || !promotionAdUnits.adUnits) {
      return this.generateNewPromotionAdUnits();
    }

    return promotionAdUnits;
  }

  // ---------------------------------------------------------------------------
  public getPromotionAdUnitsById(promotionId: string): number {
    const promotionAdUnits = LocalStorage.get(
      `${kAdUnitDisplayedPrefix}${promotionId}`,
    ).asInt();

    return promotionAdUnits ?? 0;
  }

  // ---------------------------------------------------------------------------
  public addPromotionAdUnits(
    promoId: string,
    promotionAdUnit: PromotionAdUnit,
  ) {
    const promotionAdUnits = this.getPromotionAdUnits();

    if (
      !Object.prototype.hasOwnProperty.call(promotionAdUnits.adUnits, promoId)
    ) {
      promotionAdUnits.adUnits[promoId] = promotionAdUnit;
      this.setPromotionAdUnites(promotionAdUnits);
      return;
    }

    promotionAdUnits.adUnits[promoId].lastViewTimestamp =
      promotionAdUnit.lastViewTimestamp;

    promotionAdUnits.adUnits[promoId].views++;
    this.setPromotionAdUnites(promotionAdUnits);
  }

  // ---------------------------------------------------------------------------
  private setPromotionAdUnites(promotionAdUnits: PromotionAdUnits) {
    LocalStorage.setObject(kNameOfPromotionAdUnits, promotionAdUnits);
  }

  // ---------------------------------------------------------------------------
  private generateNewPromotionAdUnits(): PromotionAdUnits {
    return { adUnits: {} };
  }
}
